import React, { createContext, useContext, useState, useEffect, useRef, use } from 'react';
import { useApiCall } from '../hooks/useApiCall';

// Create the Account Context
const AccountContext = createContext();

// Custom hook to use the Account Context
export const useAccount = () => {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return context;
};

// Account Provider Component
export const AccountProvider = ({ children }) => {

  // Initialize account from localStorage or undefined
  const [account, setAccount] = useState(() => {
    try {
      const savedAccount = localStorage.getItem('bds_account');
      return savedAccount ? JSON.parse(savedAccount) : undefined;
    } catch (error) {
      console.error('Error loading account from localStorage:', error);
      return undefined;
    }
  });

  // Save account to localStorage whenever it changes
  useEffect(() => {
    if (account) {
      localStorage.setItem('bds_account', JSON.stringify(account));
    } else {
      localStorage.removeItem('bds_account');
    }
  }, [account]);

  // Function to update account
  const updateAccount = (newAccountData) => {
    setAccount(prev => {
      if (!prev) return newAccountData;
      return { ...prev, ...newAccountData };
    });
  };

  // Function to clear account (for logout)
  const clearAccount = () => {
    setAccount(null);
    localStorage.removeItem('bds_account');
  };

  // Function to check if user is authenticated
  const isAuthenticated = () => {
    return account && account.token && account.token !== '';
  };

  const value = {
    account,
    setAccount,
    updateAccount,
    clearAccount,
    isAuthenticated
  };

  return (
    <AccountContext.Provider value={value}>
      {children}
    </AccountContext.Provider>
  );
};
